import { fmt, niceCeil, withUnit, zoneOf, ZONES } from './format';

// A strip of the odometer readings at which a part was replaced (shown for 3+ replacements)
const buildStrip = (item, t) => {
    const events = item.events || [];
    if (item.status !== 'Replaced' || events.length < 3 || !events.every(k => typeof k === 'number')) return null;

    const max = niceCeil(Math.max(...events) * 1.05);
    const pc = km => Number(((km / max) * 100).toFixed(2));

    return {
        dots: events.map(pc),
        gaps: events
            .slice(1)
            .map((km, i) => ({ left: pc((events[i] + km) / 2), label: `+${withUnit(fmt(km - events[i]), 'km')}` })),
        ticks: [0, 1, 2, 3, 4].map(k => ({ left: k * 25, label: k === 0 ? withUnit(0, 'km') : fmt((max * k) / 4) })),
        span: { left: pc(events[0]), width: Number((pc(events[events.length - 1]) - pc(events[0])).toFixed(2)) },
        aria: t('history.stripAria', { component: item.component, readings: events.map(fmt).join(', ') }),
    };
};

const buildItem = (group, item, t) => {
    const times = item.times || 1;
    let when = '';
    if (item.date) {
        const km = item.km != null ? ` · ${withUnit(fmt(item.km), 'km')}` : '';
        when = (times > 1 ? t('history.latest', { date: item.date }) : item.date) + km;
    } else if (item.km != null) {
        when = withUnit(fmt(item.km), 'km');
    }
    const quantity = item.qty ? ` × ${item.qty}` : '';
    const part = item.part ? item.part + quantity : '';

    return {
        zone: zoneOf(group, item),
        km: item.km,
        times,
        date: item.date || '',
        component: item.component,
        // the status stays English for the logic above; only what is shown is translated
        status: t(`labels.partStatus.${item.status}`, { defaultValue: item.status }),
        tagStyle: item.status === 'Work performed' ? 'warning' : 'neutral',
        timesLabel: times > 1 ? t('history.times', { count: times }) : '',
        when,
        part,
        note: part ? '' : item.note || '',
        visit: item.visit || null,
        strip: buildStrip(item, t),
    };
};

export const buildHistoryItems = (history, t) =>
    ((history && history.groups) || [])
        .filter(g => g.items && g.items.length)
        .flatMap(g => g.items.map(item => buildItem(g, item, t)));

// Areas of the car map that hold part changes, numbered in map order; flagged when a visit in them has a red flag
export const buildZones = (items, visits, selectedZone, t) => {
    const flaggedVisits = new Set(visits.filter(v => v.flag && v.n != null).map(v => v.n));

    return ZONES.map(zone => ({ zone, items: items.filter(it => it.zone === zone.k) }))
        .filter(({ items: zoneItems }) => zoneItems.length)
        .map(({ zone, items: zoneItems }, index) => {
            const changes = zoneItems.reduce((sum, it) => sum + it.times, 0);
            const latest = zoneItems.slice().sort((a, b) => (b.km || 0) - (a.km || 0))[0];
            const visitsIn = new Set(zoneItems.map(it => it.visit).filter(Boolean));
            const flag = [...visitsIn].some(n => flaggedVisits.has(n));

            return {
                key: zone.k,
                n: index + 1,
                name: t(`zones.${zone.k}`),
                geo: zone,
                items: zoneItems,
                on: selectedZone === zone.k,
                flag,
                visitsIn,
                changes,
                meta:
                    t('history.changes', { count: changes }) +
                    (latest && latest.date ? ` · ${t('history.latest', { date: latest.date })}` : ''),
            };
        });
};

// The big numbers on the stage: part changes, areas, red flags
export const buildHistoryNumbers = (zones, stats, t) => {
    if (!zones.length) return [];

    const total = zones.reduce((sum, z) => sum + z.changes, 0);
    const numbers = [
        { value: String(total), label: t('history.partChanges', { count: total }) },
        { value: String(zones.length), label: t('history.areas', { count: zones.length }) },
    ];
    if (stats && stats.redFlags != null) {
        numbers.push({
            value: String(stats.redFlags),
            label: t('history.redFlags', { count: stats.redFlags }),
            pink: stats.redFlags > 0,
        });
    }

    return numbers;
};
